import type { UnknownAction } from "@reduxjs/toolkit"
import { availabilityApi } from "@/app/appointments/availability/availabilityApi"
import { decodeJobStatus } from "@/app/appointments/availability/lib/editorModel"
import { scheduleOverridesApi } from "@/app/appointments/schedule-overrides/scheduleOverridesApi"
import { decodeBatchStatus } from "@/app/appointments/schedule-overrides/lib/bookingMoveBatch"

/** The two sonner calls a notification can raise. Typed narrowly so a test can hand in its own. */
export interface NotificationToast {
  success: (message: string, options?: { description?: string }) => unknown
  error: (message: string, options?: { description?: string }) => unknown
}

export interface NotificationContext {
  dispatch: (action: UnknownAction) => unknown
  toast: NotificationToast
  accessChanged: () => void
}

/** The `slot-plan-job` payload (SlotPlanJobNotification, AB#780): sent when an availability save lands or fails. */
export interface SlotPlanJobNotification {
  jobId: string
  /** "Succeeded" or "Failed". */
  status: string
  /** One sentence for the toast. */
  summary: string
}

function asSlotPlanJob(payload: unknown): SlotPlanJobNotification | null {
  if (!payload || typeof payload !== "object") return null
  const record = payload as Record<string, unknown>
  // SignalR's JSON protocol camel-cases; read the C# names too rather than lose a result.
  const jobId = record.jobId ?? record.JobId
  const status = record.status ?? record.Status
  const summary = record.summary ?? record.Summary
  if (typeof jobId !== "string" || typeof status !== "string") return null
  return { jobId, status, summary: typeof summary === "string" ? summary : "" }
}

/** The `booking-move-batch` payload (BookingMoveBatchNotification, AB#809): sent when a batch of moves ends. */
export interface BookingMoveBatchNotification {
  batchId: string
  /** "Succeeded" or "Failed". */
  status: string
  moved: number
  failed: number
  /** One sentence for the toast. */
  summary: string
}

function asBookingMoveBatch(payload: unknown): BookingMoveBatchNotification | null {
  if (!payload || typeof payload !== "object") return null
  const record = payload as Record<string, unknown>
  const batchId = record.batchId ?? record.BatchId
  const status = record.status ?? record.Status
  const moved = record.moved ?? record.Moved
  const failed = record.failed ?? record.Failed
  const summary = record.summary ?? record.Summary
  if (typeof batchId !== "string" || typeof status !== "string") return null
  return {
    batchId,
    status,
    moved: typeof moved === "number" ? moved : 0,
    failed: typeof failed === "number" ? failed : 0,
    summary: typeof summary === "string" ? summary : "",
  }
}

/**
 * What the app does with one hub notification. Pure apart from what it is handed, so every type is
 * tested (notificationHandler.test.ts); staffLifecycle wires it to the connection.
 *
 * - `permissions-changed`: suspend writes and refresh the checked server context.
 * - `slot-plan-job` (AB#781): an availability save finished — a toast wherever the user now is, and
 *   the job and plan caches dropped so an open Availability page shows the result.
 * - `booking-move-batch` (AB#809): a batch of appointment moves finished — a toast, and the batch,
 *   bookings and overrides caches dropped so an open page shows where everything now is.
 */
export function handleNotification(type: string, payload: unknown, { dispatch, toast, accessChanged }: NotificationContext): void {
  switch (type) {
    case "permissions-changed":
      accessChanged()
      break
    case "slot-plan-job": {
      const job = asSlotPlanJob(payload)
      if (!job) break
      const status = decodeJobStatus(job.status)
      if (status === "succeeded") {
        toast.success("Availability changes applied.", { description: job.summary || undefined })
      } else {
        toast.error("Availability changes were not fully applied.", {
          description: job.summary || "Open Appointments → Availability to see which schedules failed.",
        })
      }
      dispatch(availabilityApi.util.invalidateTags([{ type: "AmsSlotPlanJobs", id: job.jobId }, "AmsSlotPlans"]) as UnknownAction)
      break
    }
    case "booking-move-batch": {
      const batch = asBookingMoveBatch(payload)
      if (!batch) break
      const status = decodeBatchStatus(batch.status)
      if (status === "succeeded" && batch.failed === 0) {
        toast.success("Appointments moved.", { description: batch.summary || undefined })
      } else if (status === "succeeded") {
        toast.error("Some appointments could not be moved.", {
          description: batch.summary || "They are waiting in Appointments → Booking Review.",
        })
      } else {
        toast.error("The appointment moves stopped before they finished.", {
          description: batch.summary || "Those not moved are waiting in Appointments → Booking Review.",
        })
      }
      dispatch(scheduleOverridesApi.util.invalidateTags([
        { type: "AmsBookingMoveBatches", id: batch.batchId },
        "AmsBookings",
        "AmsScheduleOverrides",
      ]) as UnknownAction)
      break
    }
    default:
      // Unknown bodies/types are untrusted and can contain protected data. Do not log them.
      break
  }
}
